/**
 * Wave 29 Chunk 10 — Data Import batch detail.
 *
 * Steps shown on a single full-page surface (no modals):
 *   1. Mapping panel: target_field → source_column (with Hidro confidence)
 *   2. Dry-run preview: totals + per-row diff (with conflict resolver)
 *   3. Commit or rollback
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Database, ChevronLeft, Sparkles, Loader2, Play, CheckCircle, RotateCcw,
  AlertTriangle, Save,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import { useBranding } from "../context/TenantBrandingContext";

const OUTCOME_COLORS = {
  new: "bg-emerald-500/20 text-emerald-300",
  update: "bg-sky-500/20 text-sky-300",
  conflict: "bg-amber-500/20 text-amber-300",
  skip: "bg-zinc-500/20 text-zinc-300",
};

const STATUS_PILLS = {
  parsed: { label: "Uploaded", cls: "bg-zinc-500/20 text-zinc-200" },
  mapped: { label: "Mapped", cls: "bg-sky-500/20 text-sky-200" },
  running_dry_run: { label: "Previewing…", cls: "bg-amber-500/20 text-amber-200" },
  previewed: { label: "Previewed", cls: "bg-amber-500/20 text-amber-200" },
  running_commit: { label: "Committing…", cls: "bg-emerald-500/20 text-emerald-200" },
  committed: { label: "Committed", cls: "bg-emerald-500/20 text-emerald-200" },
  running_rollback: { label: "Rolling back…", cls: "bg-rose-500/20 text-rose-200" },
  rolled_back: { label: "Rolled back", cls: "bg-rose-500/20 text-rose-200" },
};

export default function DataImportBatch() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const isHr = user && ["owner", "admin"].includes(user.role);
  const { bid } = useParams();

  const [batch, setBatch] = useState(null);
  const [fields, setFields] = useState([]); // target field specs
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [inferring, setInferring] = useState(false);
  const [dryRunning, setDryRunning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [rolling, setRolling] = useState(false);

  const loadAll = async () => {
    setBusy(true);
    try {
      const [b, e] = await Promise.all([
        api.get(`/imports/${bid}`),
        api.get("/imports/entities"),
      ]);
      const batchData = b?.data;
      setBatch(batchData);
      const ent = (e?.data?.entities || []).find((x) => x.key === batchData.entity);
      setFields(ent?.fields || []);
      const r = await api.get(`/imports/${bid}/rows?limit=500`);
      setRows(r?.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load batch.");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user && isHr) loadAll(); /* eslint-disable-next-line */ }, [user, isHr, bid]);

  // Poll while a long-running task is in progress.
  useEffect(() => {
    if (!batch) return undefined;
    const RUNNING = ["running_dry_run", "running_commit", "running_rollback"];
    if (!RUNNING.includes(batch.status)) return undefined;
    const handle = setInterval(async () => {
      try {
        const { data } = await api.get(`/imports/${bid}`);
        setBatch(data);
        if (!RUNNING.includes(data.status)) {
          const r = await api.get(`/imports/${bid}/rows?limit=500`);
          setRows(r?.data?.items || []);
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(handle);
  }, [batch, bid]);

  // ── Auto-resume watchdog ─────────────────────────────────────────────
  // Production pods are flaky — the dry-run asyncio task gets killed by
  // kubernetes lifecycle events and the user has to keep clicking
  // "Resume preview". Instead, watch for two failure modes and re-fire
  // the dry-run automatically:
  //   1. `status === "running_dry_run"` but heartbeat is > 90 s old →
  //      the task is dead, status will never flip. Re-trigger.
  //   2. `status === "previewed"` but `sum(totals) < row_count` →
  //      task crashed without finishing. Re-trigger.
  // Capped at MAX_AUTO_RESUMES per page-load so a genuinely-broken
  // pipeline doesn't loop forever. Owner can still hit the manual
  // "Resume preview" button if the cap is reached.
  const MAX_AUTO_RESUMES = 20;
  const [autoResumeCount, setAutoResumeCount] = useState(0);
  const [autoResuming, setAutoResuming] = useState(false);
  useEffect(() => {
    if (!batch) return undefined;
    if (autoResumeCount >= MAX_AUTO_RESUMES) return undefined;
    const t = batch.totals || {};
    const rc = batch.row_count || 0;
    const sum = (t.new || 0) + (t.update || 0) + (t.conflict || 0) + (t.skip || 0);
    const isIncomplete = rc > 0 && sum < rc && batch.status !== "running_commit" && batch.status !== "running_rollback" && batch.status !== "committed";

    const heartbeat = batch.dry_run_heartbeat_at;
    let heartbeatStaleSecs = Infinity;
    if (heartbeat) {
      try { heartbeatStaleSecs = (Date.now() - new Date(heartbeat).getTime()) / 1000; } catch { /* ignore */ }
    }
    const isRunningDryRun = batch.status === "running_dry_run";

    // Case A — running_dry_run but heartbeat gone stale → task died
    const caseA = isRunningDryRun && heartbeatStaleSecs > 90;
    // Case B — settled into "previewed" / "rolled_back" but partial totals → task crashed before finishing
    const caseB = !isRunningDryRun && isIncomplete && batch.status !== "running_dry_run";
    if (!caseA && !caseB) return undefined;
    const handle = setTimeout(async () => {
      try {
        setAutoResuming(true);
        await api.post(`/imports/${bid}/dry-run`);
        setAutoResumeCount((n) => n + 1);
        // Re-fetch so the polling effect re-engages.
        const { data } = await api.get(`/imports/${bid}`);
        setBatch(data);
      } catch (e) {
        // If it fails, the manual "Resume preview" button is still there.
      } finally {
        setAutoResuming(false);
      }
    }, caseA ? 1000 : 4000);
    return () => clearTimeout(handle);
  }, [batch, bid, autoResumeCount]);

  const infer = async () => {
    setInferring(true);
    try {
      const { data } = await api.post(`/imports/${bid}/infer`, { force: true });
      setBatch(data);
      toast.success(`${aiName} proposed a mapping — review and adjust.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Infer failed.");
    } finally { setInferring(false); }
  };

  const setMappingField = (targetKey, sourceCol) => {
    setBatch((b) => ({ ...b, mapping: { ...(b?.mapping || {}), [targetKey]: sourceCol || null } }));
  };

  const toggleFillBlanksOnly = async (next) => {
    try {
      const { data } = await api.patch(`/imports/${bid}/options`, { fill_blanks_only: next });
      setBatch(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update option.");
    }
  };

  const saveMapping = async () => {
    try {
      const { data } = await api.patch(`/imports/${bid}/mapping`, { mapping: batch.mapping || {} });
      setBatch(data);
      toast.success("Mapping saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const dryRun = async () => {
    setDryRunning(true);
    try {
      await api.patch(`/imports/${bid}/mapping`, { mapping: batch.mapping || {} });
      const { data } = await api.post(`/imports/${bid}/dry-run`);
      setBatch(data);
      toast.success(`Preview running on ${batch?.row_count?.toLocaleString?.() || batch?.row_count || "?"} rows — totals will update live.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed.");
    } finally { setDryRunning(false); }
  };

  const resolveRow = async (rid, action, target_id = null) => {
    try {
      const { data } = await api.patch(`/imports/${bid}/rows/${rid}`, { action, target_id });
      setRows((rs) => rs.map((r) => (r.id === rid ? data : r)));
      // Refresh totals
      const b = await api.get(`/imports/${bid}`);
      setBatch(b?.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed.");
    }
  };

  const commit = async () => {
    if (!window.confirm("Commit this import? Every change can still be rolled back afterward.")) return;
    setCommitting(true);
    try {
      await api.post(`/imports/${bid}/commit`);
      toast.success("Commit started — progress will update live.");
      const { data } = await api.get(`/imports/${bid}`);
      setBatch(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed.");
    } finally { setCommitting(false); }
  };

  const rollback = async () => {
    if (!window.confirm("Roll back EVERY row this batch inserted/updated? Existing customers will be reverted to their previous values.")) return;
    setRolling(true);
    try {
      await api.post(`/imports/${bid}/rollback`);
      toast.success("Rollback started — progress will update live.");
      const { data } = await api.get(`/imports/${bid}`);
      setBatch(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed.");
    } finally { setRolling(false); }
  };

  const filteredRows = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.outcome === filter);
  }, [rows, filter]);

  if (!isHr) return <div className="hv-card p-6 text-sm text-[#dde3ec]">HR only.</div>;
  if (busy && !batch) {
    return <div className="hv-card p-6 text-sm text-[#9aa3b5]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading batch…</div>;
  }
  if (!batch) return null;

  const pill = STATUS_PILLS[batch.status] || { label: batch.status, cls: "bg-white/5 text-[#dde3ec]" };
  const sourceCols = batch.source_columns || [];
  const mapping = batch.mapping || {};
  const confidence = batch.confidence || {};
  const isCommitted = batch.status === "committed";
  const isRolledBack = batch.status === "rolled_back";
  const isRunning = ["running_dry_run", "running_commit", "running_rollback"].includes(batch.status);
  const canCommit = batch.status === "previewed" && (batch.totals?.conflict || 0) === 0;

  return (
    <div className="space-y-4 max-w-6xl" data-testid="import-batch-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/tools/import" className="hv-btn hv-btn-ghost text-xs" data-testid="import-batch-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 text-center">
          <Database className="w-5 h-5 text-[#1FB6C1]" /> {batch.filename || "Import batch"}
        </h1>
        <span className={"px-2 py-0.5 rounded text-[10px] font-bold " + pill.cls}>{pill.label}</span>
      </div>

      <div className="hv-card p-3 text-xs text-[#cbd5e1] flex items-center gap-2 flex-wrap" data-testid="import-batch-meta">
        <span>{batch.entity}</span>
        <span>·</span>
        <span>{batch.row_count} rows</span>
        <span>·</span>
        <span>{batch.source_columns?.length || 0} columns</span>
        <span>·</span>
        <span>{new Date(batch.created_at).toLocaleString()}</span>
        {batch.fill_blanks_only !== false ? (
          <span className="ml-auto px-2 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-200" data-testid="import-fill-blanks-on-pill">
            Safe mode: fill blanks only
          </span>
        ) : (
          <span className="ml-auto px-2 py-0.5 rounded text-[10px] bg-rose-500/20 text-rose-200" data-testid="import-fill-blanks-off-pill">
            ⚠ Overwrites existing data
          </span>
        )}
      </div>

      {/* Mapping panel */}
      {!isCommitted && !isRolledBack && !isRunning && (
        <section className="hv-card p-4" data-testid="import-mapping-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#1FB6C1]" /> Column mapping
            </h2>
            <div className="flex items-center gap-2">
              <button onClick={infer} disabled={inferring} className="hv-btn hv-btn-ghost text-xs disabled:opacity-50" data-testid="import-infer-btn">
                {inferring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Ask {aiName} to infer
              </button>
              <button onClick={saveMapping} className="hv-btn hv-btn-ghost text-xs" data-testid="import-save-mapping-btn">
                <Save className="w-3.5 h-3.5" /> Save mapping
              </button>
              <button onClick={dryRun} disabled={dryRunning} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="import-dry-run-btn">
                {dryRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                Run preview
              </button>
            </div>
          </div>
          {batch.infer_note && (
            <div className="text-[11px] text-amber-300 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {batch.infer_note}
            </div>
          )}
          <label className="flex items-start gap-2 mb-3 p-2.5 rounded-md bg-white/[0.03] border border-white/5 cursor-pointer hover:bg-white/[0.05]" data-testid="import-fill-blanks-toggle">
            <input
              type="checkbox"
              checked={batch.fill_blanks_only !== false}
              onChange={(e) => toggleFillBlanksOnly(e.target.checked)}
              className="mt-0.5"
              data-testid="import-fill-blanks-checkbox"
            />
            <div className="flex-1">
              <div className="text-sm text-white font-medium">Fill blanks only (recommended)</div>
              <div className="text-[11px] text-[#9aa3b5] mt-0.5">
                When ON: existing non-empty fields are NEVER overwritten — only currently-blank fields get filled. Safest when your current data is already correct but incomplete. Turn OFF only if you trust the imported file more than what's already in the DB.
              </div>
            </div>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {fields.map((f) => {
              const conf = confidence[f.key];
              return (
                <div key={f.key} className="flex items-center gap-2 text-xs" data-testid={`import-map-${f.key}`}>
                  <div className="w-1/2 shrink-0">
                    <div className="text-white truncate flex items-center gap-1">
                      {f.label}
                      {f.required && <span className="text-rose-400">*</span>}
                    </div>
                    <div className="text-[10px] text-[#647087]">{f.key} · {f.transformer}</div>
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                    <select
                      value={mapping[f.key] || ""}
                      onChange={(e) => setMappingField(f.key, e.target.value)}
                      className="hv-input text-xs w-full"
                      data-testid={`import-map-select-${f.key}`}
                    >
                      <option value="">— ignore —</option>
                      {sourceCols.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {typeof conf === "number" && mapping[f.key] && (
                      <span
                        className={
                          "px-1 rounded text-[10px] shrink-0 " +
                          (conf >= 0.8 ? "bg-emerald-500/20 text-emerald-200" :
                            conf >= 0.5 ? "bg-amber-500/20 text-amber-200" :
                            "bg-rose-500/20 text-rose-200")
                        }
                        title={`${aiName} confidence ${(conf * 100).toFixed(0)}%`}
                      >
                        {(conf * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Preview totals + filter */}
      {batch.totals && (
        <section className="hv-card p-4" data-testid="import-totals">
          <div className="flex items-center gap-3 flex-wrap">
            <FilterPill v="all" filter={filter} setFilter={setFilter} count={rows.length} label="All" cls="bg-white/5 text-[#dde3ec]" />
            <FilterPill v="new" filter={filter} setFilter={setFilter} count={batch.totals.new || 0} label="New" cls={OUTCOME_COLORS.new} />
            <FilterPill v="update" filter={filter} setFilter={setFilter} count={batch.totals.update || 0} label="Update" cls={OUTCOME_COLORS.update} />
            <FilterPill v="conflict" filter={filter} setFilter={setFilter} count={batch.totals.conflict || 0} label="Conflict" cls={OUTCOME_COLORS.conflict} />
            <FilterPill v="skip" filter={filter} setFilter={setFilter} count={batch.totals.skip || 0} label="Skip" cls={OUTCOME_COLORS.skip} />

            <div className="ml-auto flex items-center gap-2">
              {isRunning && (
                <span className="text-[11px] text-amber-300 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> {STATUS_PILLS[batch.status]?.label || "Running…"}
                </span>
              )}
              {!isCommitted && !isRolledBack && !isRunning && (
                <button onClick={commit} disabled={!canCommit || committing} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="import-commit-btn">
                  {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                  Commit
                </button>
              )}
              {isCommitted && (
                <button onClick={rollback} disabled={rolling} className="hv-btn hv-btn-ghost text-xs text-rose-300 disabled:opacity-50" data-testid="import-rollback-btn">
                  {rolling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Roll back
                </button>
              )}
            </div>
          </div>
          {!canCommit && !isCommitted && !isRolledBack && (batch.totals?.conflict || 0) > 0 && (
            <div className="text-[11px] text-amber-300 mt-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Resolve every conflict row below before committing.
            </div>
          )}
          {/* INCOMPLETE preview detector — when dry-run totals don't add up to row_count the
              background task was killed (uvicorn reload / pod recycle / OOM) before finishing.
              Auto-resume watchdog (above) handles this hands-free up to MAX_AUTO_RESUMES; the
              button remains as a manual escape hatch. */}
          {(() => {
            const t = batch.totals || {};
            const rc = batch.row_count || 0;
            const sum = (t.new || 0) + (t.update || 0) + (t.conflict || 0) + (t.skip || 0);
            const incomplete = rc > 0 && sum > 0 && sum < rc && !isCommitted;
            if (!incomplete) return null;
            const missing = rc - sum;
            return (
              <div className="text-[11px] text-rose-200 mt-2 flex items-center gap-2 flex-wrap" data-testid="import-incomplete-banner">
                <AlertTriangle className="w-3 h-3" />
                <span>
                  Preview is incomplete — <b>{sum.toLocaleString()}</b> of <b>{rc.toLocaleString()}</b> rows processed (<b>{missing.toLocaleString()}</b> still need scanning).
                  {autoResumeCount > 0 && (
                    <> Auto-resume tried <b>{autoResumeCount}</b>×{autoResumeCount >= 20 ? " — cap reached, click Resume preview to keep going." : "."}</>
                  )}
                  {autoResuming && <> <span className="text-amber-300">Auto-resuming…</span></>}
                </span>
                <button
                  onClick={() => { setAutoResumeCount(0); dryRun(); }}
                  disabled={dryRunning || isRunning}
                  className="hv-btn hv-btn-ghost text-[11px] py-0.5 px-2"
                  data-testid="import-resume-preview-btn"
                >
                  {dryRunning || isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  Resume preview
                </button>
              </div>
            );
          })()}
        </section>
      )}

      {/* Rows */}
      <section className="hv-card overflow-hidden" data-testid="import-rows">
        <ul className="divide-y divide-white/5">
          {filteredRows.map((r) => (
            <Row key={r.id} r={r} fields={fields} onResolve={resolveRow} disabled={isCommitted || isRolledBack} />
          ))}
          {filteredRows.length === 0 && (
            <li className="p-6 text-center text-[#9aa3b5] text-sm">No rows match the filter.</li>
          )}
        </ul>
      </section>
    </div>
  );
}

function FilterPill({ v, filter, setFilter, count, label, cls }) {
  return (
    <button
      type="button"
      onClick={() => setFilter(v)}
      className={
        "px-2.5 py-1 rounded-md text-[11px] flex items-center gap-1.5 " +
        (filter === v ? "bg-[#1FB6C1] text-black font-semibold" : "bg-white/5 text-[#dde3ec] hover:bg-white/10")
      }
      data-testid={`import-filter-${v}`}
    >
      <span>{label}</span>
      <span className={"px-1 rounded text-[10px] " + cls}>{count}</span>
    </button>
  );
}

function Row({ r, fields, onResolve, disabled }) {
  const outcome = r.chosen_action || r.outcome || "skip";
  const colorCls = OUTCOME_COLORS[outcome] || "bg-white/5 text-[#dde3ec]";
  const raw = r.raw || {};
  const norm = r.normalised || {};
  const diff = r.diff || {};
  const [showDetail, setShowDetail] = useState(false);

  return (
    <li className="px-3 sm:px-4 py-3" data-testid={`import-row-${r.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] text-[#647087] shrink-0 w-8 text-right">#{r.source_index + 1}</span>
        <span className={"px-1.5 py-0.5 rounded text-[10px] font-bold " + colorCls}>{outcome}</span>
        <span className="text-sm text-white truncate flex-1 min-w-0">
          {norm.name || raw[Object.keys(raw)[0]] || "(no name)"}
          {norm.phone && <span className="text-[#9aa3b5] text-xs ml-2">{norm.phone}</span>}
          {norm.email && <span className="text-[#9aa3b5] text-xs ml-2">{norm.email}</span>}
        </span>
        {r.outcome === "conflict" && !disabled && (
          <div className="flex items-center gap-1">
            {(r.match_candidates || []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onResolve(r.id, "update", c.id)}
                className="hv-btn hv-btn-ghost text-[10px] px-2"
                data-testid={`import-row-merge-${r.id}-${c.id}`}
                title={`Merge into ${c.name}`}
              >
                Merge → {c.name?.slice(0, 20)}
              </button>
            ))}
            <button type="button" onClick={() => onResolve(r.id, "new")} className="hv-btn hv-btn-ghost text-[10px] px-2 text-emerald-300" data-testid={`import-row-new-${r.id}`}>
              Create new
            </button>
            <button type="button" onClick={() => onResolve(r.id, "skip")} className="hv-btn hv-btn-ghost text-[10px] px-2" data-testid={`import-row-skip-${r.id}`}>
              Skip
            </button>
          </div>
        )}
        <button type="button" onClick={() => setShowDetail((s) => !s)} className="text-[10px] text-[#1FB6C1] hover:underline" data-testid={`import-row-toggle-${r.id}`}>
          {showDetail ? "Hide" : "Detail"}
        </button>
      </div>
      {r.error && (
        <div className="text-[11px] text-rose-300 mt-1 pl-12">{r.error}</div>
      )}
      {showDetail && (
        <div className="mt-2 pl-12 text-[11px] grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Raw</div>
            <table className="w-full">
              <tbody>
                {Object.entries(raw).slice(0, 12).map(([k, v]) => (
                  <tr key={k}>
                    <td className="text-[#647087] pr-2 align-top whitespace-nowrap">{k}</td>
                    <td className="text-[#dde3ec] break-all">{String(v ?? "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Normalised → Target</div>
            <table className="w-full">
              <tbody>
                {fields.filter((f) => norm[f.key] != null && norm[f.key] !== "").map((f) => (
                  <tr key={f.key}>
                    <td className="text-[#647087] pr-2 align-top whitespace-nowrap">{f.key}</td>
                    <td className="text-white break-all">
                      {String(norm[f.key])}
                      {diff[f.key] && (
                        <span className="ml-2 text-[10px] text-amber-300">
                          (was: {String(diff[f.key].before ?? "—")})
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </li>
  );
}
